import { Box, Flex } from "@rebass/grid";
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { useBrokerFeatureLoader } from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Button,
  DisplayText,
  Loading,
  Switch,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import { toast } from "@rentiohq/web-shared/dist/managers/Toast";
import styled from "@rentiohq/web-theme";
import React from "react";
import { Navigate } from "react-router-dom";
import {
  BOOKING_ANCHOR_KEY,
  BOOKING_ANCHORS,
  CRONOFY_CONNECTION_PATH,
  CRONOFY_STATUS_PATH,
  ICronofyStatus,
  readCronofyStatus,
  TBookingAnchor,
} from "utils/cronofy.utils";

// The theme's own tokens rather than two hex values, so this dropdown
// follows the design system the way the identical one on the property
// calendar does. neutral40 is #D0D0D0 and neutral0 is #FFFFFF, so nothing
// moves on screen; the colors just stop being written out twice.
const AnchorSelect = styled.select`
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.neutral40};
  background: ${({ theme }) => theme.colors.neutral0};
  font-size: 14px;
`;

export const ProfileCalendar = () => {
  const hasFetched = React.useRef(false);
  const { isFetchingBroker, hasFeature: hasCalendarSync } =
    useBrokerFeatureLoader(EBrokerFeature.CalendarSync);
  const [statusData, setStatusData] = React.useState<ICronofyStatus | null>(
    null,
  );
  // Kept beside the fetched status so the checkbox answers the click at once
  // rather than after a round trip, and is put back if the save fails.
  const [isSavingHideEmail, setIsSavingHideEmail] = React.useState(false);
  const [isSavingAnchor, setIsSavingAnchor] = React.useState(false);
  const [isSavingVisitMails, setIsSavingVisitMails] = React.useState(false);
  const [isSavingShowContents, setIsSavingShowContents] = React.useState(false);
  const [isFetchingStatus, setIsFetchingStatus] = React.useState(true);

  React.useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get("code");

    if (code && !hasFetched.current) {
      hasFetched.current = true;
      const redirectUri = window.location.origin + window.location.pathname;
      api
        .post("/cronofy/oauth-callback", {
          data: { code, redirectUri },
          isV2: true,
        })
        .then(() => {
          // Immediately wipe the ?code parameters and force a hard page sync
          // so the user visually sees the connection!
          window.location.href = window.location.pathname;
        })
        .catch((err: any) => {
          console.error("OAuth callback failed", err); // eslint-disable-line no-console
        });
    }
  }, []);

  const refetchStatus = React.useCallback(async () => {
    try {
      setIsFetchingStatus(true);
      const res = await api.get<any>(CRONOFY_STATUS_PATH, {
        isV2: true,
        params: { t: Date.now() },
      });
      setStatusData(res.data?.data);
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
    } finally {
      setIsFetchingStatus(false);
    }
  }, []);

  React.useEffect(() => {
    refetchStatus();
  }, [refetchStatus]);

  const handleConnectCalendar = async () => {
    try {
      const redirectUri = window.location.origin + window.location.pathname;
      const response = await api.get<any>(
        `/cronofy/auth-url?redirectUri=${encodeURIComponent(redirectUri)}`,
        { isV2: true },
      );
      if (response.data?.data?.url) {
        window.location.href = response.data.data.url;
      } else if (response.data?.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Failed to generate Cronofy URL", error); // eslint-disable-line no-console
    }
  };

  /**
   * The whole connection status, which is what both the read and the save
   * return since d14f4f231. Taking the save's answer as the new state keeps
   * the screen on what was stored rather than on what was asked for, and it
   * is how a field this screen did not touch stays current.
   */
  const readStatus = readCronofyStatus;

  const handleToggleHideEmail = async (nextValue: boolean) => {
    // Shown immediately, because a checkbox that waits for the server reads as
    // broken. Put back below if the save fails, so the screen never claims a
    // setting that was not stored.
    setStatusData(current =>
      current ? { ...current, hideEmailFromVisitors: nextValue } : current,
    );
    setIsSavingHideEmail(true);

    try {
      const response = await api.patch<{ data?: ICronofyStatus }>(
        CRONOFY_CONNECTION_PATH,
        {
          isV2: true,
          data: { hideEmailFromVisitors: nextValue },
        },
      );

      const saved = readStatus(response);
      if (saved) {
        setStatusData(saved);
      }
    } catch (error) {
      // Put back only this field: the other controls on this page save the
      // same status object, and a whole-object snapshot would undo them.
      setStatusData(current =>
        current ? { ...current, hideEmailFromVisitors: !nextValue } : current,
      );
      showAlert({ type: "error", error });
    } finally {
      setIsSavingHideEmail(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.delete("/cronofy/disconnect", { isV2: true });
      await refetchStatus();
    } catch (error) {
      showAlert({
        type: "error",
        error,
      });
    }
  };

  if (isFetchingBroker || isFetchingStatus) {
    return <Loading />;
  }

  if (!hasCalendarSync) {
    return <Navigate to="../personal" replace />;
  }

  /**
   * The employee's default for new visit blocks (DEV-3328).
   *
   * Stored on the user rather than on the calendar connection, which is why
   * it is shown whether or not a calendar is connected: since DEV-3320 a
   * colleague without one can still be given visit moments, so they need a
   * default as much as anyone.
   */
  const handleChangeAnchorDefault = async (next: TBookingAnchor) => {
    const previous = statusData?.visitBookingAnchorDefault;
    setStatusData(current =>
      current ? { ...current, visitBookingAnchorDefault: next } : current,
    );
    setIsSavingAnchor(true);

    try {
      const response = await api.patch<{ data?: ICronofyStatus }>(
        CRONOFY_CONNECTION_PATH,
        {
          isV2: true,
          data: { visitBookingAnchorDefault: next },
        },
      );

      const saved = readStatus(response);
      if (saved) {
        setStatusData(saved);
      }
    } catch (error) {
      setStatusData(current =>
        current ? { ...current, visitBookingAnchorDefault: previous } : current,
      );
      toast({
        heading: getLocalizedText("general.error"),
        variation: "error",
      });
    } finally {
      setIsSavingAnchor(false);
    }
  };

  /**
   * The employee's visit mails: booked, moved and canceled (DEV-3354).
   *
   * Stored on the user like the default above, so it is shown and works
   * whether or not a calendar is connected: the mails go out in both cases.
   */
  const handleToggleVisitMails = async (nextValue: boolean) => {
    setStatusData(current =>
      current ? { ...current, visitMailsEnabled: nextValue } : current,
    );
    setIsSavingVisitMails(true);

    try {
      const response = await api.patch<{ data?: ICronofyStatus }>(
        CRONOFY_CONNECTION_PATH,
        {
          isV2: true,
          data: { visitMailsEnabled: nextValue },
        },
      );

      const saved = readStatus(response);
      if (saved) {
        setStatusData(saved);
      }
    } catch (error) {
      // Only this field goes back: another setting saved meanwhile stays.
      setStatusData(current =>
        current ? { ...current, visitMailsEnabled: !nextValue } : current,
      );
      showAlert({ type: "error", error });
    } finally {
      setIsSavingVisitMails(false);
    }
  };

  /**
   * Whether colleagues see the title and location of this employee's calendar
   * events on the visit planning (DEV-3540). Events marked private in the
   * calendar itself never show, whatever this says.
   */
  const handleToggleShowContents = async (nextValue: boolean) => {
    setStatusData(current =>
      current
        ? { ...current, showCalendarContentsToColleagues: nextValue }
        : current,
    );
    setIsSavingShowContents(true);

    try {
      const response = await api.patch<{ data?: ICronofyStatus }>(
        CRONOFY_CONNECTION_PATH,
        {
          isV2: true,
          data: { showCalendarContentsToColleagues: nextValue },
        },
      );

      const saved = readStatus(response);
      if (saved) {
        setStatusData(saved);
      }
    } catch (error) {
      // Only this field goes back: another setting saved meanwhile stays.
      setStatusData(current =>
        current
          ? { ...current, showCalendarContentsToColleagues: !nextValue }
          : current,
      );
      showAlert({ type: "error", error });
    } finally {
      setIsSavingShowContents(false);
    }
  };

  const anchorDefault = statusData?.visitBookingAnchorDefault || "START";
  // Only an explicit false is off, the same rule the API sends by.
  const visitMailsEnabled = statusData?.visitMailsEnabled !== false;

  const isConnected = statusData?.isConnected;

  // One switch with its label, and its help text right under the label when
  // it has one, so every setting on the page reads the same way.
  const renderSwitch = (params: {
    isChecked: boolean;
    isDisabled: boolean;
    onChange: (value: boolean) => void;
    label: string;
    help?: string;
  }) => (
    <Flex alignItems="flex-start">
      <Box mr={2}>
        <Switch
          isChecked={params.isChecked}
          isDisabled={params.isDisabled}
          onChange={params.onChange}
        />
      </Box>
      <Box>
        <TextStyle element="div">{params.label}</TextStyle>
        {params.help && (
          <TextStyle element="div" variation="subdued" size="small">
            {params.help}
          </TextStyle>
        )}
      </Box>
    </Flex>
  );

  // Only the design system's text styles on this page: a section title, body
  // text and help text, each the same everywhere (Jonas, 2026-10-14).
  return (
    <Box p={4} style={{ maxWidth: "600px" }}>
      <DisplayText size="small" space="tight">
        {getLocalizedText("cronofy.profile.title")}
      </DisplayText>
      {/* What connecting gives you, one line each, rather than how it works. */}
      <ul style={{ margin: 0, paddingLeft: "20px", listStyle: "disc" }}>
        <li>
          <TextStyle>
            {getLocalizedText("cronofy.profile.benefit.calendar")}
          </TextStyle>
        </li>
        <li>
          <TextStyle>
            {getLocalizedText("cronofy.profile.benefit.availability")}
          </TextStyle>
        </li>
      </ul>

      <Spacer weight={ESpacerWeight.W16} />

      {isConnected ? (
        <Flex alignItems="center" flexWrap="wrap">
          <Box mr={2}>
            <TextStyle>
              {statusData.email
                ? `✅ ${getLocalizedText("cronofy.profile.connected_with", {
                    email: statusData.email,
                  })}`
                : getLocalizedText("cronofy.profile.active_sync")}
            </TextStyle>
          </Box>
          <Button appearance="outline" size="small" onClick={handleDisconnect}>
            {getLocalizedText("cronofy.profile.button.disconnect")}
          </Button>
        </Flex>
      ) : (
        <Button appearance="primary" onClick={handleConnectCalendar}>
          {getLocalizedText("cronofy.profile.button.connect")}
        </Button>
      )}

      {/* Only with a connected calendar: without one there is nothing in it
          for colleagues to see. */}
      {isConnected && (
        <>
          <Spacer weight={ESpacerWeight.W16} />
          {renderSwitch({
            isChecked: !!statusData?.showCalendarContentsToColleagues,
            isDisabled: isSavingShowContents,
            onChange: handleToggleShowContents,
            label: getLocalizedText("cronofy.profile.show_contents.label"),
            help: getLocalizedText("cronofy.profile.show_contents.info"),
          })}
        </>
      )}

      <Spacer weight={ESpacerWeight.W32} />

      {/* Everything below is stored on the user, not on the calendar
          connection, so it is shown and works with or without a connected
          calendar: a colleague without one is still given visit moments
          (DEV-3320), still gets the visit mails (DEV-3354) and still sends
          their address in them (DEV-3399). */}
      <DisplayText size="small" space="tight">
        {getLocalizedText("cronofy.profile.visits.title")}
      </DisplayText>

      <TextStyle element="div">
        {getLocalizedText("cronofy.profile.booking_anchor.label")}
      </TextStyle>
      <TextStyle element="div" variation="subdued" size="small">
        {getLocalizedText("cronofy.profile.booking_anchor.info")}
      </TextStyle>
      <Spacer weight={ESpacerWeight.W08} />
      {/* A dropdown, like the one in the block form it prefills, so the two
          read as the same setting (Jonas, 2026-10-06). */}
      <AnchorSelect
        value={anchorDefault}
        disabled={isSavingAnchor}
        onChange={event =>
          handleChangeAnchorDefault(event.target.value as TBookingAnchor)
        }
      >
        {BOOKING_ANCHORS.map(anchor => (
          <option key={anchor} value={anchor}>
            {getLocalizedText(BOOKING_ANCHOR_KEY[anchor])}
          </option>
        ))}
      </AnchorSelect>

      <Spacer weight={ESpacerWeight.W24} />

      {renderSwitch({
        isChecked: visitMailsEnabled,
        isDisabled: isSavingVisitMails,
        onChange: handleToggleVisitMails,
        label: getLocalizedText("cronofy.profile.visit_mails.label"),
      })}

      <Spacer weight={ESpacerWeight.W16} />

      {renderSwitch({
        isChecked: !!statusData?.hideEmailFromVisitors,
        isDisabled: isSavingHideEmail,
        onChange: handleToggleHideEmail,
        label: getLocalizedText("cronofy.profile.hide_email.label"),
        help: getLocalizedText("cronofy.profile.hide_email.reply_info"),
      })}
    </Box>
  );
};
